import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/data';

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const article = articles.find((a) => a.id === Number(id));
  const currentIndex = articles.findIndex((a) => a.id === Number(id));
  const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;
  const relatedArticles = articles.filter((a) => a.category === article?.category && a.id !== article?.id).slice(0, 3);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="px-4 md:px-6 py-20 text-center">
          <i className="ri-error-warning-line text-6xl text-foreground-300 mb-4 block"></i>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-6">您访问的文章不存在或已被删除</p>
          <Link to="/articles" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full text-sm hover:bg-primary-600 transition-all">
            <i className="ri-arrow-left-line"></i>
            返回文章列表
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryName = article.category === 'football' ? '足球' : article.category === 'basketball' ? '篮球' : article.category === 'tennis' ? '网球' : '电竞';

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    url: `https://huangguan-sports.com/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '皇冠体育',
      url: 'https://huangguan-sports.com',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('breadcrumb.home'), item: 'https://huangguan-sports.com/' },
      { '@type': 'ListItem', position: 2, name: t('article.list_title'), item: 'https://huangguan-sports.com/articles' },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 皇冠体育`}
        description={article.excerpt}
        keywords={`${categoryName},皇冠体育,赛事分析,竞猜攻略,${article.title}`}
        canonicalUrl={`https://huangguan-sports.com/article/${article.id}`}
        ogType="article"
      />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="px-4 md:px-6 py-6">
          <article className="max-w-4xl mx-auto">
            <Breadcrumb items={[
              { label: t('article.list_title'), href: '/articles' },
              { label: article.title },
            ]} />

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 text-xs text-foreground-400 mb-4 flex-wrap">
                <span className="px-2.5 py-1 rounded-full bg-primary-50 text-primary-600 font-medium">
                  {categoryName}
                </span>
                <span>{article.date}</span>
                <span>{article.author}</span>
                <span>{article.views.toLocaleString()} 阅读</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
            </header>

            {/* Featured Image */}
            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-base md:prose-lg max-w-none mb-12 text-foreground-800 leading-relaxed
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
                [&_p]:mb-5 [&_p]:text-foreground-700 [&_p]:leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>

            {/* Prev / Next Navigation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 pt-8 border-t border-background-200">
              {prevArticle ? (
                <Link
                  to={`/article/${prevArticle.id}`}
                  className="group flex items-start gap-3 p-4 rounded-xl border border-background-200 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-foreground-400 group-hover:text-primary-500 transition-colors mt-1"></i>
                  <div>
                    <span className="text-xs text-foreground-400">{t('article.prev_article')}</span>
                    <p className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-1 mt-0.5">
                      {prevArticle.title}
                    </p>
                  </div>
                </Link>
              ) : (
                <div></div>
              )}
              {nextArticle ? (
                <Link
                  to={`/article/${nextArticle.id}`}
                  className="group flex items-start justify-end gap-3 p-4 rounded-xl border border-background-200 hover:border-primary-200 transition-all cursor-pointer text-right"
                >
                  <div>
                    <span className="text-xs text-foreground-400">{t('article.next_article')}</span>
                    <p className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-1 mt-0.5">
                      {nextArticle.title}
                    </p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 transition-colors mt-1"></i>
                </Link>
              ) : (
                <div className="hidden sm:block"></div>
              )}
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mb-10">
                <h3 className="font-heading font-bold text-xl text-foreground-900 mb-5">
                  {t('article.related')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <span className="text-xs text-foreground-400">{rel.date}</span>
                        <h4 className="font-medium text-foreground-800 text-sm mt-1 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {rel.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}